import { BridgeComponent } from "@hotwired/strada"

// Notifications push des apps natives (PushNotifications::PromptComponent). L'app répond à
// « connect » et à « request » par l'état des notifications sur ce téléphone :
//   { status: "authorized" | "notDetermined" | "denied" | "unavailable", token, platform, appId, environment }
// La page enregistre le jeton pour le compte connecté et montre l'état qui convient.
export default class extends BridgeComponent {
  static component = "push-notifications"
  static targets = ["state"]
  static values = { registerUrl: String, registeredToken: String, showAuthorized: Boolean }

  connect() {
    super.connect()
    // Au démarrage à froid, la page se connecte avant que l'app ne branche le pont
    // (`data-bridge-components` sur <html>) : on attend qu'il le soit.
    this.bridgeObserver = new MutationObserver(() => this.start())
    this.bridgeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-bridge-components"]
    })
    // Un rafraîchissement morphé remet la carte cachée sans reconnecter le contrôleur ; au retour
    // dans l'app, les notifications ont pu être activées dans les réglages.
    this.refreshState = () => this.refresh()
    document.addEventListener("turbo:morph", this.refreshState)
    document.addEventListener("visibilitychange", this.refreshState)
    this.start()
  }

  disconnect() {
    this.bridgeObserver?.disconnect()
    document.removeEventListener("turbo:morph", this.refreshState)
    document.removeEventListener("visibilitychange", this.refreshState)
    // Strada abandonne la réponse attendue : déplacée dans le DOM (la cloche passe sous <body>),
    // la carte redemande l'état en se reconnectant.
    this.started = false
    super.disconnect()
  }

  start() {
    if (this.started || !this.enabled) return

    this.started = true
    this.bridgeObserver.disconnect()
    this.send("connect", {}, (message) => this.update(message.data))
  }

  refresh() {
    if (document.visibilityState !== "visible") return
    if (!this.started) return this.start()

    if (this.status) this.show(this.status)
    this.send("connect", {}, (message) => this.update(message.data))
  }

  request() {
    this.send("request", {}, (message) => this.update(message.data))
  }

  openSettings() {
    this.send("openSettings")
  }

  update({ status, token, platform, appId, environment } = {}) {
    this.show(status)
    if (status === "authorized" && token) this.register({ token, platform, appId, environment })
  }

  show(status) {
    this.status = status
    const visible = status === "notDetermined" || status === "denied" || (status === "authorized" && this.showAuthorizedValue)

    this.element.hidden = !visible
    this.stateTargets.forEach((target) => {
      target.hidden = target.dataset.state !== status
    })
  }

  async register({ token, platform, appId, environment }) {
    if (token === this.registeredTokenValue || this.registering) return

    this.registering = true
    try {
      const response = await fetch(this.registerUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: JSON.stringify({ token, platform, app_id: appId, environment })
      })
      if (response.ok && (await response.json()).registered) this.registeredTokenValue = token
    } catch {
      // Réseau coupé : la prochaine page réessaiera.
    } finally {
      this.registering = false
    }
  }
}
